/**
 * widgets/KPICard.ts · KPI 卡组件 (13 *Home 都用)
 *
 * 设计原则:
 *   - 唯一组件 · 不让多 IIFE 各自写 KPI 卡 HTML
 *   - 统一样式: 蓝渐变 banner + 4 色 (蓝/绿/橙/红/紫)
 *   - min-height:80px + contain:layout (base CSS · 防 CLS)
 *   - iOS HIG 触屏友好
 */

export interface KPI {
  label: string;
  value: string | number;
  color?: 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'cyan';
  trend?: 'up' | 'down' | 'flat';
  hint?: string;
}

const COLOR_MAP: Record<NonNullable<KPI['color']>, string> = {
  blue: '#1e40af',
  green: '#10b981',
  red: '#dc2626',
  orange: '#f59e0b',
  purple: '#7c3aed',
  cyan: '#0ea5e9',
};

export function renderKPIGrid(kpis: KPI[]): HTMLElement {
  const grid = document.createElement('div');
  grid.className = 'tn-kpi-grid grid gap-3 mb-4';
  grid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(180px, 1fr))';

  for (const kpi of kpis) {
    grid.appendChild(renderKPI(kpi));
  }
  return grid;
}

export function renderKPI(kpi: KPI): HTMLElement {
  const color = COLOR_MAP[kpi.color || 'blue'];
  const card = document.createElement('div');
  card.className = 'tn-kpi-card';
  card.style.borderLeftColor = color;
  card.innerHTML = `
    <div class="tn-kpi-label">${escapeHtml(String(kpi.label))}</div>
    <div class="tn-kpi-value" style="color: ${color}">${escapeHtml(String(kpi.value))}</div>
    ${kpi.hint ? `<div class="tn-kpi-hint">${escapeHtml(kpi.hint)}</div>` : ''}
  `;
  return card;
}

/**
 * v351 · 进阶式披露 KPI grid · 3-5 主 KPI + 二级折叠
 * 遵循 2026 dashboard 趋势: 限制顶层 KPI 数量, 二级数据放折叠区
 */
export function renderKPIWithDisclosure(primary: KPI[], secondary?: KPI[]): HTMLElement {
  const section = document.createElement('div');
  section.className = 'tn-kpi-section';
  section.setAttribute('data-expanded', 'false');

  // 主 KPI grid (限 5 个)
  const primaryGrid = document.createElement('div');
  primaryGrid.className = 'tn-kpi-grid grid gap-3 mb-2';
  primaryGrid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(180px, 1fr))';
  for (const kpi of primary.slice(0, 5)) {
    primaryGrid.appendChild(renderKPI(kpi));
  }
  section.appendChild(primaryGrid);

  // 二级 KPI (折叠默认隐藏)
  if (secondary && secondary.length > 0) {
    const toggle = document.createElement('button');
    toggle.className = 'tn-kpi-toggle';
    toggle.textContent = `▼ 显示更多 (${secondary.length}) · Hiện thêm`;
    toggle.addEventListener('click', () => {
      const expanded = section.getAttribute('data-expanded') === 'true';
      section.setAttribute('data-expanded', expanded ? 'false' : 'true');
      toggle.textContent = expanded
        ? `▼ 显示更多 (${secondary.length}) · Hiện thêm`
        : `▲ 收起 · Thu gọn`;
    });
    section.appendChild(toggle);

    const secondaryGrid = document.createElement('div');
    secondaryGrid.className = 'tn-kpi-secondary tn-kpi-grid grid gap-3';
    secondaryGrid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(160px, 1fr))';
    for (const kpi of secondary) {
      secondaryGrid.appendChild(renderKPI(kpi));
    }
    section.appendChild(secondaryGrid);
  }

  return section;
}

/**
 * Page header · 蓝渐变 banner (13 home + 28 list 都用)
 */
export function renderPageHeader(emoji: string, title: string, subtitle?: string): HTMLElement {
  const header = document.createElement('div');
  header.className = 'tn-page-header rounded-lg p-4 mb-4 text-white shadow-md';
  header.style.background = 'linear-gradient(135deg, #1e40af, #3b82f6)';
  header.innerHTML = `
    <h1 class="text-xl font-bold">${emoji} ${escapeHtml(title)}</h1>
    ${subtitle ? `<p class="text-sm opacity-90 mt-1">${escapeHtml(subtitle)}</p>` : ''}
  `;
  return header;
}

/**
 * CTA button grid · 13 home 都用
 */
export interface CTA {
  icon: string;
  label: string;
  page: string;
  primary?: boolean;
}

export function renderCTAGrid(ctas: CTA[]): HTMLElement {
  const grid = document.createElement('div');
  grid.className = 'tn-cta-grid grid gap-2';
  grid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(180px, 1fr))';

  for (const cta of ctas) {
    const btn = document.createElement('button');
    btn.className = 'tn-cta-btn rounded-lg p-3 text-left text-white font-semibold shadow-sm transition-transform hover:-translate-y-0.5';
    btn.style.background = cta.primary
      ? 'linear-gradient(135deg, #3b82f6, #1e40af)'
      : 'linear-gradient(135deg, #6366f1, #4338ca)';
    btn.style.minHeight = '44px'; // iOS HIG
    btn.innerHTML = `<span class="text-base mr-1">${cta.icon}</span> ${escapeHtml(cta.label)}`;
    btn.addEventListener('click', () => {
      window.location.hash = `#${cta.page}`;
    });
    grid.appendChild(btn);
  }
  return grid;
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]!));
}
